import { assertNever } from 'owa-assert';
import { ClientAttachmentId, MailboxType } from 'owa-client-ids';
import * as OwaFilesUrl from 'owa-files-url';
import { getDownloadToken } from 'owa-files-url';
import AttachmentUrlType from 'owa-files-url/lib/schema/AttachmentUrlType';
import { getOwaCanaryCookie } from 'owa-service/lib/canary';
import AttachmentType from 'owa-service/lib/contract/AttachmentType';
import LinkAttachment from 'owa-service/lib/contract/LinkAttachment';
import ReferenceAttachment from 'owa-service/lib/contract/ReferenceAttachment';
import FetchPreviewUrlRequest from '../types/FetchPreviewUrlRequest';

// Get the Url for cloudy attachments.
export function getAttachmentUrlForCloudy(
    clientAttachmentId: ClientAttachmentId,
    attachment: AttachmentType,
    attachmentUrlType: AttachmentUrlType,
    isReadOnly: boolean,
    includeCanary: boolean = true
) {
    const {
        AttachmentId: attachmentId,
        Name: name,
        OriginalPermissionType: originalPermissionType,
        PermissionType: permissionType,
    } = <ReferenceAttachment>attachment;

    const longPathName: string | null = tryGetAttachmentUrl(attachment);

    const isGroups: boolean = clientAttachmentId.mailboxInfo.type === MailboxType.GroupMailbox;
    const groupsAddress: string = isGroups
        ? clientAttachmentId.mailboxInfo.primarySmtpAddress + '/'
        : null;

    switch (attachmentUrlType) {
        case AttachmentUrlType.FullFile:
        case AttachmentUrlType.Print:
            return OwaFilesUrl.getAttachmentUrlForCloudy(
                attachmentId,
                longPathName,
                attachmentUrlType,
                isReadOnly,
                permissionType,
                originalPermissionType,
                includeCanary,
                name,
                groupsAddress
            );
        case AttachmentUrlType.Preview:
            if ((<ReferenceAttachment>attachment).AttachmentPreviewUrl) {
                return (<ReferenceAttachment>attachment).AttachmentPreviewUrl;
            } else {
                return OwaFilesUrl.getAttachmentUrlForCloudy(
                    attachmentId,
                    longPathName,
                    AttachmentUrlType.Preview,
                    isReadOnly,
                    permissionType,
                    originalPermissionType,
                    includeCanary,
                    name,
                    groupsAddress
                );
            }
        case AttachmentUrlType.Thumbnail:
            return (<ReferenceAttachment>attachment).AttachmentThumbnailUrl;
        default:
            return assertNever(attachmentUrlType);
    }
}

// Get the download Url for classic attachments.
export function getAttachmentUrlForClassic(
    attachmentId: ClientAttachmentId,
    attachmentUrlType: AttachmentUrlType,
    addIsDownloadQueryParam: boolean = false,
    includeDownloadToken: boolean = true
) {
    return OwaFilesUrl.getAttachmentUrlForClassic(
        attachmentId.mailboxInfo.routingKey,
        attachmentId,
        attachmentUrlType,
        addIsDownloadQueryParam,
        includeDownloadToken
    );
}

/*
 * Initialize the preview URL
 * @returns {FetchPreviewUrl} The request to fetch the preview url
 */
export function getPreviewUrlRequest(
    attachmentId: ClientAttachmentId,
    attachment: AttachmentType,
    isCloudy: boolean,
    isReadyOnly: boolean,
    addIsDownloadQueryParam: boolean = false
): FetchPreviewUrlRequest {
    const previewUrl = isCloudy
        ? getAttachmentUrlForCloudy(
              attachmentId,
              attachment,
              AttachmentUrlType.Preview,
              isReadyOnly,
              false /* includeCanary */
          )
        : getAttachmentUrlForClassic(
              attachmentId,
              AttachmentUrlType.Preview,
              addIsDownloadQueryParam,
              false /* includeDownloadToken */
          );

    if (isCloudy) {
        return {
            url: previewUrl,
            init: {
                credentials: 'include',
            },
        };
    } else {
        // If the attachment is classic then we send the download token
        // as header
        const token = getDownloadToken();
        const headers = new Headers();
        headers.set('X-Token', token);
        headers.set('X-OWA-CANARY', getOwaCanaryCookie());
        return {
            url: previewUrl,
            init: {
                headers: headers,
            },
        };
    }
}

export function tryGetAttachmentUrl(attachment: AttachmentType): string | null {
    let attachmentUrl = null;
    attachmentUrl = (attachment as ReferenceAttachment).AttachLongPathName;
    if (!attachmentUrl) {
        attachmentUrl = (attachment as LinkAttachment).WebUrl;
    }
    return attachmentUrl;
}

export default function getAttachmentUrl(
    attachmentId: ClientAttachmentId,
    attachment: AttachmentType,
    attachmentUrlType: AttachmentUrlType,
    isCloudy: boolean,
    isReadyOnly: boolean,
    addIsDownloadQueryParam: boolean = false
): string {
    const downloadUrl = isCloudy
        ? getAttachmentUrlForCloudy(attachmentId, attachment, attachmentUrlType, isReadyOnly)
        : getAttachmentUrlForClassic(attachmentId, attachmentUrlType, addIsDownloadQueryParam);
    return downloadUrl;
}
